<script lang="ts" setup>
//components
import ProductList from './ProductList.vue';
import ProductNoItem from './ProductNoItem.vue';
import ProductSearch from './ProductSearch.vue';
import ProductNotFound from './ProductNotFound.vue';
import ProductSkeletons from './ProductSkeletons.vue';
import ProductMessage from './ProductPickerMessage.vue';

//composable
import { useScrollData } from '../composable/useScrollData';
import { useProductPickerStore } from '../composable/useProductPickerStore';
import { usePickProduct } from '../composable/usePickProduct';

//hooks
const {
  setRefresh,
  fetchProducts,
  resetProductData,
  setCurrentEditModule,
  query,
  isLoading,
  isRefresh,
  fetchedAll,
  queryList,
  productsList,
  fetchedAllQuery,
  moduleIdInStore,
} = useProductPickerStore();
const { getCurrentPickedProduct, getCurrentModuleId } = usePickProduct();
const { observeScroll, perfectScrollbarOptions, rowStyle, wrapperStyle, currentData } = useScrollData();

const scrollBarRef = ref<HTMLElement | null>();

//emits
const emits = defineEmits<{
  (event: 'close'): void;
}>();

//life cycles
onMounted(() => {
  const currentModuleId = getCurrentModuleId();

  if (!moduleIdInStore.value) {
    setCurrentEditModule(currentModuleId);
    getProducts();
  }

  const isEditingNewModule = moduleIdInStore.value != currentModuleId;

  if (isEditingNewModule) {
    resetProductData();
    setCurrentEditModule(currentModuleId);
    getProducts();
  } else if (productsList.value.length === 0) {
    getProducts();
  }
});

//computed
const isProductNotFound = computed(() => {
  return !productsList.value.length && fetchedAll.value === true;
});

const isNoItem = computed(() => {
  const fetched = fetchedAllQuery.value;
  const list = queryList.value;
  return query.value.length !== 0 && fetched && !list.length;
});

//methods
const getProducts = () => {
  getCurrentPickedProduct(() => fetchProducts());
};

const refreshData = () => {
  if (isLoading.value || isRefresh.value) return;
  setRefresh(true);
  resetProductData();
  getProducts();
};
</script>

<template>
  <keep-alive>
    <Modal
      classs
      c-class="product-picker w-[600px]"
      h-class="bg-light1 p-0 rounded-t-[3px]"
      m-class="product-picker_body h-[620px] rounded-b-[3px] d-flex flex-column"
      f-class="p-0"
      @cancel="emits('close')">
      <template #header>
        <Teleport to="#tooltips">
          <Tooltip :pointer="true" :width="'auto'" :delay="800" />
        </Teleport>
        <div class="d-flex align-items-center justify-content-between px-2 py-[18px]">
          <p class="heading-16-medium text-light-high">Manually select a product</p>
          <div class="d-flex align-items-center">
            <div
              class="clickable d-flex h-[24px] w-[24px] items-center justify-center rounded-[3px] hover:bg-[#e2e2e2]"
              @click="refreshData">
              <img v-if="isRefresh" class="w-[16px] animate-spin" src="/static/icons/loading.svg" alt="" />
              <i
                v-else
                v-tooltip="{ content: 'Refresh', placement: 'bottom' }"
                class="icon icon-iconwrapper text-light-high"></i>
            </div>
            <div class="divider mx-1 h-[16px] border-l-[1px] border-[#d6d6d6]"></div>
            <div
              class="clickable d-flex h-[24px] w-[24px] items-center justify-center rounded-[3px] hover:bg-[#e2e2e2]"
              @click="emits('close')">
              <i class="icon icon-iconwrapper1 text-light-high"></i>
            </div>
          </div>
        </div>
      </template>
      <template #default>
        <template v-if="isProductNotFound">
          <ProductNotFound />
        </template>
        <template v-else>
          <div class="sticky top-0 left-0 z-[2] bg-[#eeeeee] p-2">
            <ProductMessage />
            <ProductSearch :scroll-bar-ref="scrollBarRef" />
          </div>
          <div v-if="isLoading" class="d-flex flex-column">
            <ProductSkeletons v-for="index in 5" :key="index" custom-class="mx-2" />
          </div>
          <ProductNoItem v-else-if="isNoItem" :message="'No products match your search.'" />
          <ProductList v-else />
        </template>
      </template>
    </Modal>
  </keep-alive>
</template>

<style lang="scss" scoped></style>
